/**
 * ProjectLayout — progressive disclosure layout for the project workspace.
 *
 * Main pane shows the DAG by default. Drag any sidebar tab onto the
 * main pane to promote its content there (bare, no tab bar). The DAG
 * then appears as a selectable tab in the sidebar. Drag the "DAG" tab
 * back onto the main pane (or use the demote callback) to restore.
 *
 * The divider between panes is draggable to resize.
 * Options menu in SlidePanel allows flipping sidebar left/right.
 */

import { useCallback, useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";
import { useProvenanceContext, type PanelTab } from "../../context/ProvenanceContext";
import { useViewState } from "../../context/ViewStateContext";
import DAGPanel from "../panels/DAGPanel";
import DataExplorerPanel from "../panels/DataExplorerPanel";
import PlotBuilderPanel from "../panels/PlotBuilderPanel";
import type { PlotBuilderConfig } from "../plot-builder/types";
import SavedPlotsPanel from "../panels/SavedPlotsPanel";
import ArtifactDetailPanel from "../panels/ArtifactDetailPanel";
import Flow360ViewerPanel from "../panels/Flow360ViewerPanel";
import ReproducibilityPanel from "../panels/ReproducibilityPanel";
import ConfigurationPanel from "../panels/ConfigurationPanel";
import ConversationsPanel from "../panels/ConversationsPanel";
import ForkPanel from "../../flow360/ForkPanel";
import GeometryPanel from "../panels/GeometryPanel";
import CampaignPanel from "../panels/CampaignPanel";
import { VersionHistoryPanel } from "../versioning/VersionHistoryPanel";
import { createPlot, type PlotScope } from "../../api/plots";
import { SlidePanel } from "./SlidePanel";
import { DRAG_TAB_TYPE } from "./panelDrag";

interface Props {
  projectId: string;
}

const DEFAULT_SPLIT = 42;
const MIN_SPLIT = 20;
const MAX_SPLIT = 80;

export function ProjectLayout({ projectId }: Props) {
  const { openPanel, setOpenPanel, currentBranch } = useProvenanceContext();
  const isOpen = openPanel !== null;

  const [promotedTab, setPromotedTab] = useState<PanelTab>(null);
  const [loadedPlotConfig, setLoadedPlotConfig] = useState<PlotBuilderConfig | undefined>();
  const [sidebarLeft, setSidebarLeft] = useState(false);
  const [splitPct, setSplitPct] = useState(DEFAULT_SPLIT);
  const [dragOverMain, setDragOverMain] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const draggingResize = useRef(false);

  // -- Register layout state for sharing --
  const { register } = useViewState();
  const promotedTabRef = useRef(promotedTab);
  promotedTabRef.current = promotedTab;
  const sidebarLeftRef = useRef(sidebarLeft);
  sidebarLeftRef.current = sidebarLeft;
  const splitPctRef = useRef(splitPct);
  splitPctRef.current = splitPct;

  useEffect(() => register(
    "layout",
    () => ({
      promotedTab: promotedTabRef.current,
      sidebarLeft: sidebarLeftRef.current,
      splitPct: splitPctRef.current,
    }),
    (state) => {
      if (state.promotedTab !== undefined) setPromotedTab(state.promotedTab as PanelTab);
      if (state.sidebarLeft !== undefined) setSidebarLeft(state.sidebarLeft as boolean);
      if (state.splitPct !== undefined) setSplitPct(state.splitPct as number);
    },
  ), [register]);

  const handlePromote = useCallback((tabId: string) => {
    if (tabId === "dag") {
      setPromotedTab(null);
      return;
    }
    setPromotedTab(tabId as PanelTab);
    // If the promoted tab isn't the active sidebar tab, switch sidebar
    // to DAG so the user sees the DAG in the sidebar.
    setOpenPanel("dag");
  }, [setOpenPanel]);

  const handleDemote = useCallback(() => {
    const prev = promotedTab;
    setPromotedTab(null);
    if (prev) setOpenPanel(prev);
  }, [promotedTab, setOpenPanel]);

  const toggleSidebarSide = useCallback(() => {
    setSidebarLeft(prev => !prev);
    setSplitPct(prev => 100 - prev);
  }, []);

  // --- Resize ---
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    draggingResize.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingResize.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    setSplitPct(Math.max(MIN_SPLIT, Math.min(MAX_SPLIT, pct)));
  }, []);

  const handlePointerUp = useCallback(() => {
    draggingResize.current = false;
  }, []);

  // --- Drop on main pane ---
  const handleDragOver = useCallback((e: DragEvent) => {
    if (e.dataTransfer.types.includes(DRAG_TAB_TYPE)) {
      e.preventDefault();
      setDragOverMain(true);
    }
  }, []);
  const handleDragLeave = useCallback(() => setDragOverMain(false), []);
  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDragOverMain(false);
    const tabId = e.dataTransfer.getData(DRAG_TAB_TYPE);
    if (tabId) handlePromote(tabId);
  }, [handlePromote]);

  const handleSavePlot = useCallback(
    async (config: PlotBuilderConfig, data: Record<string, unknown>[], _thumbnail?: string, scope?: PlotScope) => {
      const nodeIds = [
        ...new Set(
          data
            .map((r) => r._node_id as string | undefined)
            .filter((id): id is string => !!id),
        ),
      ];
      await createPlot({
        project_id: projectId,
        title: config.title || "Untitled Plot",
        chart_type: config.chartType,
        config: config as unknown as Record<string, unknown>,
        data_snapshot: data,
        source_node_ids: nodeIds,
        branch: currentBranch,
        scope: scope ?? (currentBranch === "main" ? "project" : "branch"),
      });
    },
    [projectId, currentBranch],
  );

  const handleLoadPlot = useCallback((config: PlotBuilderConfig, _data: Record<string, unknown>[]) => {
    setLoadedPlotConfig(config);
    setOpenPanel("plot");
  }, [setOpenPanel]);

  function renderPromotedContent(): ReactNode {
    switch (promotedTab) {
      case "data": return <DataExplorerPanel projectId={projectId} />;
      case "plot": return <PlotBuilderPanel projectId={projectId} onSave={handleSavePlot} initialConfig={loadedPlotConfig} />;
      case "saved-plots": return <SavedPlotsPanel projectId={projectId} onLoadPlot={handleLoadPlot} />;
      case "detail": return <ArtifactDetailPanel />;
      case "viewer": return <Flow360ViewerPanel projectId={projectId} />;
      case "cockpit": return <ForkPanel projectId={projectId} />;
      case "history": return <VersionHistoryPanel projectId={projectId} />;
      case "config": return <div style={{ padding: 16, overflow: "auto", height: "100%" }}><ConfigurationPanel projectId={projectId} /></div>;
      case "repro": return <div style={{ padding: 16, overflow: "auto", height: "100%" }}><ReproducibilityPanel projectId={projectId} /></div>;
      case "conversations": return <ConversationsPanel />;
      case "geometry": return <div style={{ padding: 16, overflow: "auto", height: "100%" }}><GeometryPanel projectId={projectId} /></div>;
      case "campaign": return <CampaignPanel projectId={projectId} />;
      default: return null;
    }
  }

  const mainContent = promotedTab ? renderPromotedContent() : <DAGPanel projectId={projectId} />;

  const mainSplit = isMaximized ? "0%" : isOpen ? `${sidebarLeft ? 100 - splitPct : splitPct}%` : "100%";

  const dropOverlay = dragOverMain && (
    <div style={{
      position: "absolute", inset: 0, background: "var(--accent-a3)", zIndex: 10,
      display: "flex", alignItems: "center", justifyContent: "center",
      pointerEvents: "none", borderRadius: 4,
    }}>
      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--accent-11)" }}>
        {promotedTab ? "Drop to swap" : "Drop to promote"}
      </span>
    </div>
  );

  const mainPaneEl = !isMaximized && (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        flexBasis: mainSplit,
        flexShrink: 0, flexGrow: 0,
        transition: draggingResize.current ? "none" : "flex-basis 300ms ease",
        overflow: "hidden", minWidth: 0, position: "relative",
        outline: dragOverMain ? "2px dashed var(--accent-9)" : "none",
        outlineOffset: -2,
      }}
    >
      {mainContent}
      {dropOverlay}
    </div>
  );

  const sidePaneEl = isOpen && (
    <div style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
      <SlidePanel
        projectId={projectId}
        promotedTab={promotedTab}
        onDemote={handleDemote}
        sidebarLeft={sidebarLeft}
        onToggleSidebarSide={toggleSidebarSide}
        isMaximized={isMaximized}
        onToggleMaximize={() => setIsMaximized(prev => !prev)}
      />
    </div>
  );

  const resizeHandle = isOpen && !isMaximized && (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      style={{
        width: 8, flexShrink: 0, cursor: "col-resize",
        background: draggingResize.current ? "var(--accent-a5)" : "transparent",
        borderLeft: "1px solid var(--gray-a4)",
        transition: "background 150ms", position: "relative", zIndex: 5,
      }}
      onMouseEnter={(e) => { if (!draggingResize.current) (e.currentTarget as HTMLElement).style.background = "var(--gray-a3)"; }}
      onMouseLeave={(e) => { if (!draggingResize.current) (e.currentTarget as HTMLElement).style.background = "transparent"; }}
    >
      <div style={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: 3, height: 32, borderRadius: 2,
        background: "var(--gray-a6)", opacity: 0.7,
      }} />
    </div>
  );

  return (
    <div ref={containerRef} style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
      {sidebarLeft ? (
        <>{sidePaneEl}{resizeHandle}{mainPaneEl}</>
      ) : (
        <>{mainPaneEl}{resizeHandle}{sidePaneEl}</>
      )}
    </div>
  );
}
